import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  Divider
} from "@mui/material";
import { authFetch } from "../auth/session";

// Expresión regular para encontrar variables entre llaves
const PROMPT_VARIABLE_REGEX = /\{([^{}]+)\}/g;

// Recibe agentPrompt como prop
function ChatView({ agentId, agentPrompt }) { 
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  // Estados para los parámetros dinámicos
  const [promptParams, setPromptParams] = useState({}); // { key: value }
  // const [paramKeys, setParamKeys] = useState([]); // ['key1', 'key2'] // Reemplazado por useMemo

  // Claves de parámetros extraídas (memoizado)
  const paramKeys = useMemo(() => {
    if (!agentPrompt) return [];
    const keys = new Set();
    let match;
    // Usar matchAll para obtener todas las coincidencias de una vez
    for (const match of agentPrompt.matchAll(PROMPT_VARIABLE_REGEX)) {
      keys.add(match[1].trim()); // match[1] es el contenido dentro de {}
    }
    return Array.from(keys);
  }, [agentPrompt]); // Solo recalcula si agentPrompt cambia

  // Efecto para inicializar/limpiar los valores de los parámetros
  useEffect(() => {
    setPromptParams(prevParams => {
      const newParams = {};
      paramKeys.forEach(key => {
        // Mantener valor anterior si la clave sigue existiendo, sino inicializar
        newParams[key] = paramKeys.includes(key) ? (prevParams[key] || '') : '';
      });
      // Limpiar claves antiguas que ya no existen en paramKeys
      Object.keys(prevParams).forEach(oldKey => {
        if (!paramKeys.includes(oldKey)) {
          // Opcional: podrías querer eliminar la clave aquí si causa problemas,
          // pero normalmente no es necesario ya que no se usarán.
        }
      });
      return newParams;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKeys]); // Se ejecuta cuando cambia la lista de claves (evitar añadir prevParams)

  // Manejador para actualizar el valor de un parámetro
  const handleParamChange = (key, value) => {
    setPromptParams(prevParams => ({ ...prevParams, [key]: value }));
  };

  const handleSendChat = async () => {
    if (!userInput.trim()) return;

    const currentUserMessage = { sender: "user", text: userInput.trim() };
    setMessages(prev => [...prev, currentUserMessage]);
    const currentInput = userInput;
    setUserInput("");
    setChatLoading(true);

    // Construir el payload
    const payload = {
      query: currentInput.trim(),
      agent_id: agentId,
      parameter_prompt: paramKeys.length > 0 ? promptParams : null, 
      conversation_id: "", // Podría gestionarse un ID real si se necesita historial
      metadata_filter: null
    };

    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/conversational-engine/handle-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      let agentResponseText = "Error: No se recibió respuesta válida.";
      if (data?.text) {
        agentResponseText = data.text;
      } else if (data?.error) { 
        agentResponseText = `Error del API: ${data.error}`;
      } 
      setMessages(prev => [...prev, { sender: "agent", text: agentResponseText }]);

    } catch (error) {
      console.error("Error fetching chat response:", error); // Loguear error
      setMessages(prev => [...prev, { sender: "agent", text: "Error de red: " + error.message }]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Probar Chat con {agentId}
      </Typography>

      {/* Sección para Parámetros del Prompt */}
      {paramKeys.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 'medium' }}>
              Parámetros del Prompt:
            </Typography>
            <Grid container spacing={2}>
              {paramKeys.map((key) => (
                <Grid item xs={12} sm={6} md={6} key={key}> 
                  <TextField
                    fullWidth
                    multiline 
                    maxRows={4} 
                    size="small"
                    label={key} 
                    value={promptParams[key] || ''} 
                    onChange={(e) => handleParamChange(key, e.target.value)}
                    placeholder={`Valor para {${key}}...`}
                    disabled={chatLoading} // Deshabilitar también estos campos durante la carga
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {/* Área de Mensajes del Chat */}
      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: 1,
          p: 2,
          minHeight: 200,
          maxHeight: 350,
          overflowY: "auto",
          mb: 2,
          bgcolor: 'background.paper' // Usar color de fondo del tema
        }}
      >
        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
              mb: 1
            }}
          >
            {/* Burbuja de Mensaje */}
            <Paper 
              elevation={1} // Añadir ligera sombra
              sx={{
                bgcolor: msg.sender === "user" ? "primary.main" : "grey.200", // Usar color del tema
                color: msg.sender === "user" ? "primary.contrastText" : "text.primary",
                borderRadius: msg.sender === "user" ? '16px 16px 4px 16px' : '16px 16px 16px 4px', // Estilo burbuja
                p: 1.5,
                maxWidth: "75%" // Un poco más ancho
              }}
            >
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                {msg.text}
              </Typography>
            </Paper>
          </Box>
        ))}

        {/* Loader */}
        {chatLoading && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
            <Paper elevation={1} sx={{ bgcolor: "grey.200", borderRadius: '16px 16px 16px 4px', p: 1.5 }}>
              <CircularProgress size={20} />
            </Paper>
          </Box>
        )}
      </Box>

      {/* Input y Botón de Enviar */}
      <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Escribe un mensaje..."
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !chatLoading && userInput.trim()) { 
              e.preventDefault();
              handleSendChat();
            }
          }}
          disabled={chatLoading}
          sx={{ flexGrow: 1 }}
        />
        <Button 
          variant="contained" 
          onClick={handleSendChat} 
          disabled={chatLoading || !userInput.trim()}
          sx={{ flexShrink: 0 }} // Evitar que el botón se encoja
        >
          {chatLoading ? <CircularProgress size={24} color="inherit" /> : "Enviar"}
        </Button>
      </Box>
    </Box>
  );
}

export default ChatView;
